import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  getOfflineCacheStorage,
  isOfflineCacheSupported,
  type OfflineCacheMeta,
  type OfflineCacheRecord,
  type OfflineSessionSnapshot,
} from './cacheDb';
import { keepMailOnDevice } from './keepOnDevice';
import { isPersistableKey, isStaleRender, trimForPersist } from './whitelist';

/**
 * Persisting the query cache, deliberately and within bounds.
 *
 * ## Why this is hand-rolled
 *
 * TanStack ships `@tanstack/query-persist-client-core`, and it was not used.
 * Two reasons, in order:
 *
 * 1. **It persists the cache as one blob.** Restoring is all-or-nothing, and
 *    so is eviction: there is no way to drop the oldest message bodies and
 *    keep the folder tree, which is exactly the bound this feature needs. A
 *    record per query gives real oldest-first eviction.
 * 2. It would be a new dependency for ~80 lines of `IDBRequest` plumbing the
 *    codebase already writes by hand in `api/deviceKey.ts`.
 *
 * ## The budget
 *
 * Three caps, all from docs/plan/06-frontend-v1.md §1:
 *
 * - **{@link MAX_ENTRIES} records** (500),
 * - **{@link MAX_CACHE_BYTES}** (50 MB) of estimated payload,
 * - **{@link MAX_RECORD_AGE_MS}** (7 days) per record.
 *
 * At a cap, the oldest records go first — oldest by `dataUpdatedAt`, i.e. when
 * the server last answered for them. Nothing is ever refused a write because
 * the cache is full: the write lands and the sweep that follows makes room, so
 * the mail you just read is the mail that survives. Expired records are dropped
 * on the sweep *and* skipped on restore, so a cache that has sat untouched for
 * a fortnight shows nothing rather than something fortnight-old.
 */

/** Records. 500 message bodies plus their lists is a working mailbox offline. */
export const MAX_ENTRIES = 500;
/** Estimated payload bytes across every record of every account. */
export const MAX_CACHE_BYTES = 50 * 1024 * 1024;
/** Nothing older than this is kept, or restored. */
export const MAX_RECORD_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Cache events arrive in bursts; one write per burst. */
export const FLUSH_DEBOUNCE_MS = 1_000;

export interface OfflineCacheOptions {
  owner: string;
  email: string;
  /** Overridable for tests; production always uses {@link MAX_CACHE_BYTES}. */
  maxBytes?: number;
}

/** Why the persisted cache is or is not gone after a sign-out. */
export type StoragePurgeOutcome =
  | 'purged'
  /** This browser has no usable IndexedDB, so nothing was ever written. */
  | 'unsupported'
  /** Asked, and the deletion did not happen — another tab still holds it open. */
  | 'failed';

interface Running {
  client: QueryClient;
  options: OfflineCacheOptions;
  unsubscribe: () => void;
  dirty: Set<string>;
  timer: ReturnType<typeof setTimeout> | undefined;
  pending: Promise<void> | undefined;
  lastSyncedAt: number;
  session: OfflineSessionSnapshot | undefined;
}

let running: Running | undefined;

/** Stable, order-insensitive id for a query key, matching TanStack's hashing. */
function hashKey(key: readonly unknown[]): string {
  return JSON.stringify(key, (_field, value: unknown) =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? Object.keys(value as Record<string, unknown>)
          .sort()
          .reduce<Record<string, unknown>>((acc, field) => {
            acc[field] = (value as Record<string, unknown>)[field];
            return acc;
          }, {})
      : value,
  );
}

function recordId(owner: string, key: readonly unknown[]): string {
  return `${owner}\u0000${hashKey(key)}`;
}

/** Approximate UTF-16 payload size. Wrong by a little; never wrong by a lot. */
function estimateBytes(data: unknown): number {
  try {
    return JSON.stringify(data).length * 2;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * The session, minus anything that identifies the token.
 *
 * `sessionId` is the first 16 hex characters of the token *hash* — not a
 * credential, and still not something worth leaving on a shared disk when
 * nothing offline reads it.
 */
function snapshotSession(session: unknown): OfflineSessionSnapshot | undefined {
  if (typeof session !== 'object' || session === null) return undefined;
  const { sessionId: _sessionId, ...rest } = session as Record<string, unknown>;
  const account = (rest as { account?: { id?: unknown; email?: unknown } }).account;
  if (typeof account?.id !== 'string' || typeof account.email !== 'string') return undefined;
  return rest as unknown as OfflineSessionSnapshot;
}

function scheduleFlush(state: Running): void {
  if (state.timer !== undefined) return;
  state.timer = setTimeout(() => {
    state.timer = undefined;
    void flushOfflineCache();
  }, FLUSH_DEBOUNCE_MS);
}

/**
 * Start persisting this client's whitelisted queries for this owner.
 *
 * Returns the teardown. Starting twice replaces the first — there is one cache
 * and one signed-in owner at a time.
 */
export function startOfflineCache(client: QueryClient, options: OfflineCacheOptions): () => void {
  stopOfflineCache();
  if (!isOfflineCacheSupported()) return () => undefined;
  // "Keep mail on this device" is off (ADR 0060). `OfflineCacheProvider` is
  // what reacts to the setting and does the deleting; this is the floor, so
  // that no future caller can start a persister behind the user's back.
  if (!keepMailOnDevice()) return () => undefined;

  const cache = client.getQueryCache();
  const state: Running = {
    client,
    options,
    dirty: new Set<string>(),
    timer: undefined,
    pending: undefined,
    lastSyncedAt: 0,
    session: undefined,
    unsubscribe: () => undefined,
  };

  const note = (key: readonly unknown[]): void => {
    if (key[0] === 'session' && key.length === 1) {
      const snapshot = snapshotSession(client.getQueryData(queryKeys.session()));
      if (snapshot !== undefined) {
        state.session = snapshot;
        scheduleFlush(state);
      }
      return;
    }
    if (!isPersistableKey(key)) return;
    state.dirty.add(hashKey(key));
    scheduleFlush(state);
  };

  state.unsubscribe = cache.subscribe((event) => {
    if (event.query.state.status !== 'success') return;
    note(event.query.queryKey);
  });

  // Anything already in the cache when we start — an account switch, or a
  // restore that happened before the session resolved.
  for (const query of cache.getAll()) {
    if (query.state.status === 'success') note(query.queryKey);
  }

  running = state;
  return () => {
    if (running === state) stopOfflineCache();
  };
}

export function stopOfflineCache(): void {
  if (running === undefined) return;
  if (running.timer !== undefined) clearTimeout(running.timer);
  running.unsubscribe();
  running = undefined;
}

/**
 * Write everything noted since the last flush, then bring the store back
 * inside its budget. Exported so tests — and sign-out — need not wait on a
 * debounce timer.
 */
export async function flushOfflineCache(): Promise<void> {
  const state = running;
  if (state === undefined) return;
  // One flush at a time: two overlapping sweeps would race over the same
  // eviction list and could delete a record the other just wrote.
  state.pending = (state.pending ?? Promise.resolve()).then(() => writeOnce(state));
  await state.pending;
}

async function writeOnce(state: Running): Promise<void> {
  if (running !== state) return;
  const storage = getOfflineCacheStorage();
  if (storage === undefined) return;

  const dirty = [...state.dirty];
  state.dirty.clear();
  const session = state.session;
  state.session = undefined;
  if (dirty.length === 0 && session === undefined) return;

  const cache = state.client.getQueryCache();
  const byHash = new Map<string, OfflineCacheRecord>();
  for (const query of cache.getAll()) {
    const hash = hashKey(query.queryKey);
    if (!dirty.includes(hash)) continue;
    if (query.state.status !== 'success' || query.state.data === undefined) continue;
    const data = trimForPersist(query.state.data);
    const updatedAt = query.state.dataUpdatedAt || Date.now();
    byHash.set(hash, {
      id: recordId(state.options.owner, query.queryKey),
      owner: state.options.owner,
      key: query.queryKey as readonly unknown[],
      data,
      updatedAt,
      bytes: estimateBytes(data),
    });
    if (updatedAt > state.lastSyncedAt) state.lastSyncedAt = updatedAt;
  }

  const records = [...byHash.values()].filter((record) => Number.isFinite(record.bytes));

  try {
    if (records.length > 0) await storage.write(records);
    if (running !== state) return;

    const meta: OfflineCacheMeta = {
      owner: state.options.owner,
      email: state.options.email,
      lastSyncedAt: state.lastSyncedAt || Date.now(),
      session: session ?? (await storage.readMeta())?.session,
    };
    await storage.writeMeta(meta);
    if (running !== state) return;
    await sweep(storage, state.options.maxBytes ?? MAX_CACHE_BYTES);
  } catch {
    // A full disk, a blocked store, a database another tab is upgrading.
    // The app is not worse off than before the write; nothing surfaces.
  }
}

async function sweep(
  storage: NonNullable<ReturnType<typeof getOfflineCacheStorage>>,
  maxBytes: number,
): Promise<void> {
  const index = await storage.readIndex();
  const cutoff = Date.now() - MAX_RECORD_AGE_MS;
  const doomed = new Set<string>();

  for (const entry of index) {
    if (entry.updatedAt < cutoff) doomed.add(entry.id);
  }

  // Oldest first — `updatedAt` is when the server last answered for it.
  const live = index
    .filter((entry) => !doomed.has(entry.id))
    .sort((a, b) => a.updatedAt - b.updatedAt);
  let bytes = live.reduce((sum, entry) => sum + entry.bytes, 0);
  let count = live.length;

  for (const entry of live) {
    // Never evict the last record: a cache of one oversized message body is
    // still more useful than an empty one, and the alternative is an eviction
    // loop that deletes everything it is handed.
    if (count <= 1) break;
    if (count <= MAX_ENTRIES && bytes <= maxBytes) break;
    doomed.add(entry.id);
    bytes -= entry.bytes;
    count -= 1;
  }

  if (doomed.size > 0) await storage.remove([...doomed]);
}

/* ------------------------------------------------------------------ reading */

export interface RestoreResult {
  restored: number;
  /** The newest `dataUpdatedAt` restored, or undefined when nothing was. */
  lastSyncedAt: number | undefined;
}

/**
 * Put the last known mail back into a cold client.
 *
 * Restored data carries its original `dataUpdatedAt`, so TanStack treats it as
 * what it is — stale — and refetches it the moment there is a connection.
 * Nothing here pretends to be fresh.
 */
export async function restoreOfflineCache(
  client: QueryClient,
  owner: string,
): Promise<RestoreResult> {
  // Off means off in both directions: a cache written before the setting was
  // turned off (or by a tab that has not noticed yet) is not read back either.
  // The provider deletes it; this makes sure nothing serves it in the meantime.
  if (!keepMailOnDevice()) return { restored: 0, lastSyncedAt: undefined };
  const storage = getOfflineCacheStorage();
  if (storage === undefined) return { restored: 0, lastSyncedAt: undefined };

  let rows: OfflineCacheRecord[];
  try {
    rows = await storage.readAll(owner);
  } catch {
    return { restored: 0, lastSyncedAt: undefined };
  }

  // A message body made under another sanitizer version is expired too:
  // served, it would render without what the current rules keep (ADR 0081).
  const cutoff = Date.now() - MAX_RECORD_AGE_MS;
  const gone = (row: OfflineCacheRecord) => row.updatedAt < cutoff || isStaleRender(row.key, row.data);
  const expired = rows.filter(gone);
  const fresh = rows.filter((row) => !gone(row));

  if (expired.length > 0) {
    try {
      await storage.remove(expired.map((row) => row.id));
    } catch {
      /* the restore is what matters; the sweep will try again */
    }
  }

  let newest: number | undefined;
  for (const row of fresh) {
    // Never overwrite something the live app has already fetched.
    if (client.getQueryData(row.key as QueryKey) !== undefined) continue;
    client.setQueryData(row.key as QueryKey, row.data, { updatedAt: row.updatedAt });
    if (newest === undefined || row.updatedAt > newest) newest = row.updatedAt;
  }

  return { restored: fresh.length, lastSyncedAt: newest };
}

/**
 * Put the offline session snapshot back, so a cold start with no connection
 * renders the app instead of "temporarily unavailable".
 *
 * Only ever called while the browser is offline. With a network,
 * `GET /auth/session` is the authority and this must not pre-empt it: a
 * session the server has revoked would otherwise look alive.
 */
export async function restoreOfflineSession(
  client: QueryClient,
): Promise<OfflineSessionSnapshot | undefined> {
  if (!keepMailOnDevice()) return undefined;
  const meta = await readOfflineMeta();
  const session = meta?.session;
  if (session === undefined) return undefined;
  if (client.getQueryData(queryKeys.session()) !== undefined) return undefined;
  client.setQueryData(queryKeys.session(), session);
  return session;
}

export async function readOfflineMeta(): Promise<OfflineCacheMeta | undefined> {
  const storage = getOfflineCacheStorage();
  if (storage === undefined) return undefined;
  try {
    return await storage.readMeta();
  } catch {
    return undefined;
  }
}

/* ------------------------------------------------------------------ purging */

/**
 * Delete every byte of persisted mail, and stop the persister with it.
 *
 * Called from `session/teardown.ts`, next to the service worker's HTTP cache
 * purge. Stopping first is not a detail: a persister still subscribed to a
 * client that has not been cleared yet would write the mailbox straight back
 * into the database this just deleted.
 *
 * Deliberately **not** gated on "keep mail on this device" (ADR 0060). A
 * device with the setting off may still be holding a cache written before it
 * was turned off, or by a version of this app that predates the setting; a
 * sign-out that skipped the delete because "nothing should be there" would be
 * exactly the wrong thing to be clever about. It is also what the `'purged'`
 * that sign-out reports to the user means.
 */
export async function purgeOfflineCache(): Promise<StoragePurgeOutcome> {
  stopOfflineCache();
  const storage = getOfflineCacheStorage();
  if (storage === undefined) return 'unsupported';
  try {
    return (await storage.destroy()) ? 'purged' : 'failed';
  } catch {
    return 'failed';
  }
}
